import React, { useState, useEffect } from 'react'
import styles from './CreateChatModal.module.scss'

const CreateChatModal = ({ isOpen, onClose, onSubmit, categories = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    room_type: 'public', // public, private, voice, voice_private
    category: ''
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [createdRoom, setCreatedRoom] = useState(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Название чата обязательно'
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Описание обязательно'
    }

    if (!formData.room_type) {
      newErrors.room_type = 'Выберите тип чата'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        room_type: formData.room_type,
        category: formData.category || null
      }

      const result = await onSubmit(submitData)

      // Для приватных комнат показываем экран с кодом приглашения
      if (
        result &&
        (formData.room_type === 'private' || formData.room_type === 'voice_private') &&
        result.invite_code
      ) {
        setCreatedRoom(result)
        setFormData({ name: '', description: '', room_type: 'public', category: '' })
        setErrors({})
      } else {
        setFormData({ name: '', description: '', room_type: 'public', category: '' })
        setErrors({})
        onClose()
      }
    } catch (error) {
      console.error('Ошибка создания чата:', error)
      setErrors({
        submit: error?.response?.data?.detail || error?.response?.data?.error || 'Ошибка создания чата'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  // Экран успеха — показываем код приглашения
  if (createdRoom) {
    return (
      <div className={styles.modalOverlay} onClick={() => { setCreatedRoom(null); onClose() }} data-lenis-prevent>
        <div className={styles.modal} onClick={e => e.stopPropagation()}>
          <div className={styles.successScreen}>
            <div className={styles.successIcon}>🎉</div>
            <h2 className={styles.successTitle}>Чат создан!</h2>
            <p className={styles.successDesc}>
              Комната <strong>{createdRoom.name}</strong> успешно создана.
            </p>
            <div className={styles.inviteCodeBlock}>
              <p className={styles.inviteCodeLabel}>🔗 Код приглашения:</p>
              <div className={styles.inviteCodeRow}>
                <code className={styles.inviteCodeText}>{createdRoom.invite_code}</code>
                <button
                  className={styles.copyBtn}
                  onClick={() => {
                    navigator.clipboard.writeText(createdRoom.invite_code)
                    setCopied(true)
                    setTimeout(() => setCopied(false), 2000)
                  }}
                >
                  {copied ? '✓ Скопировано' : '📋 Копировать'}
                </button>
              </div>
              <p className={styles.inviteNote}>
                Поделитесь этим кодом с теми, кого хотите пригласить. Код также доступен в карточке чата.
              </p>
            </div>
            <button
              className={styles.submitButton}
              onClick={() => { setCreatedRoom(null); onClose() }}
            >
              Перейти к чатам
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick} data-lenis-prevent>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>Создать новый чат</h2>
          <button 
            className={styles.closeButton}
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="name">
              Название чата <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Введите название чата"
              className={errors.name ? styles.inputError : ''}
              disabled={loading}
            />
            {errors.name && (
              <span className={styles.errorMessage}>{errors.name}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description">
              Описание <span className={styles.required}>*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Опишите чат"
              rows={4}
              className={errors.description ? styles.inputError : ''}
              disabled={loading}
            />
            {errors.description && (
              <span className={styles.errorMessage}>{errors.description}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="room_type">
              Тип чата <span className={styles.required}>*</span>
            </label>
            <select
              id="room_type"
              name="room_type"
              value={formData.room_type}
              onChange={handleInputChange}
              className={errors.room_type ? styles.inputError : ''}
              disabled={loading}
            >
              <option value="public">🌍 Публичный — доступен всем</option>
              <option value="private">🔒 Приватный — только по приглашению</option>
              <option value="voice">🎤 Голосовой — публичный с голосом</option>
              <option value="voice_private">🔒🎤 Приватный голосовой — голос по приглашению</option>
            </select>
            {errors.room_type && (
              <span className={styles.errorMessage}>{errors.room_type}</span>
            )}
            {(formData.room_type === 'private' || formData.room_type === 'voice_private') && (
              <p className={styles.typeHint}>
                🔒 Приватная комната — после создания будет сгенерирован код приглашения, который вы сможете передать другим.
              </p>
            )}
          </div>

          {categories.length > 0 && (
            <div className={styles.formGroup}>
              <label htmlFor="category">Категория (необязательно)</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value="">Выберите категорию</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {errors.submit && (
            <div className={styles.submitError}>
              {errors.submit}
            </div>
          )}

          <div className={styles.formActions}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
              disabled={loading}
            >
              Отмена
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={loading}
            >
              {loading ? 'Создание...' : 'Создать чат'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateChatModal
